/**
 * NGO Handout Card — 1/4 page (4.25" × 5.5") portrait, double-sided.
 *
 * At 300 DPI print: 1275 × 1650 px per side.
 * Screen preview scales down; PNG/PDF export renders at native size.
 *
 * Palette: Forest Pine · Terracotta · Honey Gold on Bone Canvas
 * (matches the new AADC design system — see /app/design_guidelines.json).
 *
 * Component is prop-driven so NGO partners can pass their own
 * ngoLogoSrc + ngoName to co-brand.
 */
import React, { forwardRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';

const CARD_W = 1275;   // 4.25" @ 300 DPI
const CARD_H = 1650;   // 5.5"  @ 300 DPI

// Brand tokens
const FOREST = '#1E4731';
const FOREST_INK = '#17261D';
const FOREST_DEEP = '#0F2C1E';
const TERRACOTTA = '#C95A2B';
const HONEY = '#ECA02B';
const BONE = '#F9F6F0';
const BONE_WARM = '#FBEFD2';
const BONE_BORDER = '#D9D3C4';
const MUTED_INK = '#4A5A50';

const FONT_HEAD = "'Playfair Display', 'Georgia', serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
const FONT_MONO = "'JetBrains Mono', monospace";

// --- Shared paw-print watermark, muted for bone bg ---
const PawWatermark = () => (
  <svg
    className="absolute inset-0"
    style={{ opacity: 0.045 }}
    width="100%"
    height="100%"
    viewBox={`0 0 ${CARD_W} ${CARD_H}`}
    preserveAspectRatio="xMidYMid slice"
    aria-hidden="true"
  >
    <defs>
      <pattern id="paws-forest" x="0" y="0" width="180" height="180" patternUnits="userSpaceOnUse">
        <g fill={FOREST}>
          <circle cx="90" cy="110" r="24" />
          <ellipse cx="58" cy="76" rx="13" ry="18" />
          <ellipse cx="122" cy="76" rx="13" ry="18" />
          <ellipse cx="34" cy="106" rx="10" ry="15" />
          <ellipse cx="146" cy="106" rx="10" ry="15" />
        </g>
      </pattern>
    </defs>
    <rect width={CARD_W} height={CARD_H} fill="url(#paws-forest)" />
  </svg>
);

const XGlyph = ({ size = 26 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
    <path fill="currentColor" d="M18.244 2H21l-6.51 7.44L22 22h-6.828l-4.79-6.26L4.8 22H2.04l6.98-7.98L2 2h6.914l4.32 5.71L18.244 2Zm-2.4 18h1.66L7.24 4H5.48l10.364 16Z" />
  </svg>
);

const TikTokGlyph = ({ size = 26 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
    <path fill="currentColor" d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.79A8.16 8.16 0 0 0 21 10.35V6.94a4.85 4.85 0 0 1-1.41-.25Z" />
  </svg>
);

// ---------------------------------------------------------------------------
// FRONT — inviting, hero-forward, big QR
// ---------------------------------------------------------------------------
export const HandoutCardFront = forwardRef(function HandoutCardFront(
  {
    themeLabel = 'A member-owned clubhouse',
    headline = 'Welcome home.',
    subhead = 'Every dog deserves a community.',
    pillars = [
      { title: 'Your Voice', hint: 'You vote. You decide.' },
      { title: 'Your Data', hint: 'Records, DNA, ancestry — yours.' },
      { title: 'Your Pack', hint: 'Real dog friends nearby.' },
      { title: 'Your Perks', hint: 'Deals that pay you back.' },
    ],
    logoSrc = '/branding/princess/princess-mark-180.png',
    heroSrc = '/branding/princess/princess-portrait-512.png',
    ctaLine = 'Scan for your free year',
    website = 'americandogclub.com',
    qrTarget,
    ngoName,
    ngoLogoSrc,
  },
  ref
) {
  const qrValue = qrTarget || `https://${website.replace(/^https?:\/\//, '')}`;

  return (
    <div
      ref={ref}
      style={{
        width: `${CARD_W}px`,
        height: `${CARD_H}px`,
        transform: 'scale(0.3)',
        transformOrigin: 'top left',
        backgroundColor: BONE,
        fontFamily: FONT_BODY,
      }}
      className="relative overflow-hidden shadow-2xl"
      data-testid="handout-card-front"
    >
      <PawWatermark />

      {/* Top Forest Pine band with logo mark + club name */}
      <div
        className="absolute top-0 left-0 right-0 flex items-center px-14 gap-6"
        style={{ height: '140px', background: `linear-gradient(90deg, ${FOREST_DEEP} 0%, ${FOREST} 55%, ${FOREST_DEEP} 100%)` }}
      >
        <div
          className="rounded-full overflow-hidden shrink-0"
          style={{ width: '100px', height: '100px', backgroundColor: BONE, boxShadow: `0 0 0 4px ${BONE}` }}
        >
          <img src={logoSrc} alt="Club logo" crossOrigin="anonymous" className="w-full h-full object-cover" />
        </div>
        <div style={{ color: BONE }}>
          <div style={{ fontSize: '24px', letterSpacing: '0.35em', fontFamily: FONT_MONO }} className="uppercase font-medium leading-none">
            American
          </div>
          <div style={{ fontSize: '48px', lineHeight: 1, fontFamily: FONT_HEAD, color: HONEY }} className="italic mt-1.5">
            Dog Club
          </div>
        </div>
      </div>

      {/* Hero portrait */}
      <div className="absolute" style={{ top: '180px', left: '50%', transform: 'translateX(-50%)' }}>
        <div
          className="rounded-full overflow-hidden shadow-xl"
          style={{
            width: '420px',
            height: '420px',
            backgroundColor: BONE_WARM,
            boxShadow: `0 0 0 8px rgba(236,160,43,0.35), 0 10px 30px -8px rgba(23,38,29,0.3)`,
          }}
        >
          <img src={heroSrc} alt="Club mascot" crossOrigin="anonymous" className="w-full h-full object-cover" />
        </div>
      </div>

      {/* Headline block */}
      <div className="absolute text-center px-14" style={{ top: '635px', left: 0, right: 0 }}>
        <div
          style={{
            fontSize: '20px',
            color: TERRACOTTA,
            letterSpacing: '0.4em',
            fontFamily: FONT_MONO,
          }}
          className="uppercase font-semibold"
        >
          {themeLabel}
        </div>
        <h1
          style={{ fontSize: '82px', lineHeight: 1, color: FOREST, marginTop: '14px', fontFamily: FONT_HEAD }}
          className="italic"
        >
          {headline}
        </h1>
        <p
          style={{ fontSize: '30px', marginTop: '18px', color: FOREST_INK, fontFamily: FONT_BODY }}
          className="font-medium"
        >
          {subhead}
        </p>
      </div>

      {/* 2×2 pillar grid */}
      <div
        className="absolute grid grid-cols-2 gap-x-8 gap-y-6 px-14"
        style={{ top: '890px', left: 0, right: 0 }}
        data-testid="handout-front-pillars"
      >
        {pillars.map((p, i) => (
          <div
            key={i}
            className="rounded-2xl flex flex-col items-center text-center"
            style={{
              border: `3px solid ${TERRACOTTA}`,
              padding: '20px 24px',
              backgroundColor: BONE,
            }}
          >
            <div style={{ fontSize: '38px', color: FOREST, lineHeight: 1, fontFamily: FONT_HEAD }} className="italic">
              {p.title}
            </div>
            {p.hint && (
              <div style={{ fontSize: '20px', color: MUTED_INK, marginTop: '8px', lineHeight: 1.25, fontFamily: FONT_BODY }}>
                {p.hint}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* QR + CTA */}
      <div className="absolute" style={{ bottom: '110px', left: '50%', transform: 'translateX(-50%)' }}>
        <div className="flex flex-col items-center gap-3">
          <div
            className="rounded-md shadow-md"
            style={{ padding: '14px', backgroundColor: BONE, border: `2px solid ${HONEY}` }}
            data-testid="handout-front-qr"
          >
            <QRCodeSVG value={qrValue} size={260} bgColor={BONE} fgColor={FOREST} level="M" includeMargin={false} />
          </div>
          <div style={{ fontSize: '30px', color: FOREST, fontFamily: FONT_HEAD }} className="italic font-semibold">
            {ctaLine}
          </div>
          <div style={{ fontSize: '22px', color: TERRACOTTA, letterSpacing: '0.15em', fontFamily: FONT_MONO }}>
            {website}
          </div>
        </div>
      </div>

      {/* NGO co-brand strip (optional) */}
      {(ngoName || ngoLogoSrc) && (
        <div
          className="absolute flex items-center justify-center gap-4"
          style={{
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: BONE_WARM,
            borderTop: `2px solid ${BONE_BORDER}`,
            padding: '14px 56px',
          }}
          data-testid="handout-front-ngo"
        >
          {ngoLogoSrc && (
            <img src={ngoLogoSrc} alt={ngoName || 'NGO'} crossOrigin="anonymous" className="h-10 w-auto object-contain" />
          )}
          {ngoName && (
            <div style={{ fontSize: '18px', color: FOREST, fontFamily: FONT_HEAD }} className="italic">
              Gifted by {ngoName}
            </div>
          )}
        </div>
      )}
    </div>
  );
});

// ---------------------------------------------------------------------------
// BACK — the pitch: 4 selling points + how-to-join + co-brand footer
// ---------------------------------------------------------------------------
export const HandoutCardBack = forwardRef(function HandoutCardBack(
  {
    pitchHeadline = 'What you can do',
    points = [
      { title: 'Speak up', body: 'Members write the rules and elect the officers.' },
      { title: 'Save up', body: 'Member perks that pay for themselves — insurance, gear, vet.' },
      { title: 'Show up', body: 'Local walks, events, and meets with real dog people.' },
      { title: 'Team up', body: 'Health records, DNA, and ancestry — one shared home for your dog.' },
    ],
    priceFloor = 'Free year courtesy of your adoption — no card required',
    recapItems = [
      'Member-Governed',
      "Your Dog's Records",
      'Real Local Community',
      'Perks That Pay Back',
    ],
    website = 'americandogclub.com',
    xHandle,
    tiktokHandle,
    logoSrc = '/branding/princess/princess-mark-180.png',
    footerNote = 'Scan the QR on the front to claim your free membership year.',
    // NGO co-brand block
    ngoName,
    ngoAddress,
    ngoPhone,
    ngoEmail,
    ngoWebsite,
    ngoLogoSrc,
    ngoTagline,
  },
  ref
) {
  const hasNgo = !!(ngoName || ngoLogoSrc || ngoAddress || ngoPhone || ngoEmail || ngoWebsite);

  return (
    <div
      ref={ref}
      style={{
        width: `${CARD_W}px`,
        height: `${CARD_H}px`,
        transform: 'scale(0.3)',
        transformOrigin: 'top left',
        backgroundColor: BONE,
        fontFamily: FONT_BODY,
      }}
      className="relative overflow-hidden shadow-2xl"
      data-testid="handout-card-back"
    >
      <PawWatermark />

      {/* Header */}
      <div className="absolute" style={{ top: '90px', left: '80px', right: '80px' }}>
        <div className="flex items-center gap-5 mb-8">
          <div
            className="rounded-full overflow-hidden shrink-0"
            style={{
              width: '80px',
              height: '80px',
              backgroundColor: BONE_WARM,
              boxShadow: `0 0 0 4px rgba(236,160,43,0.4)`,
            }}
          >
            <img src={logoSrc} alt="Club logo" crossOrigin="anonymous" className="w-full h-full object-cover" />
          </div>
          <div>
            <div style={{ fontSize: '18px', color: TERRACOTTA, letterSpacing: '0.35em', fontFamily: FONT_MONO }} className="uppercase font-medium">
              American Dog Club
            </div>
            <div style={{ fontSize: '40px', lineHeight: 1.1, color: FOREST, fontFamily: FONT_HEAD }} className="italic">
              {pitchHeadline}
            </div>
          </div>
        </div>

        {/* 4 numbered selling points */}
        <div className="space-y-6" data-testid="handout-back-points">
          {points.map((pt, i) => (
            <div key={i} className="flex gap-5">
              <div
                className="rounded-full flex items-center justify-center font-bold shrink-0"
                style={{
                  width: '46px',
                  height: '46px',
                  fontSize: '24px',
                  backgroundColor: TERRACOTTA,
                  color: BONE,
                  fontFamily: FONT_MONO,
                }}
              >
                {i + 1}
              </div>
              <div className="flex-1 pt-1">
                <div style={{ fontSize: '28px', color: FOREST, lineHeight: 1.1, fontFamily: FONT_HEAD }} className="font-semibold">
                  {pt.title}
                </div>
                <div style={{ fontSize: '19px', color: MUTED_INK, lineHeight: 1.4, marginTop: '4px', fontFamily: FONT_BODY }}>
                  {pt.body}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pricing floor band */}
      <div
        className="absolute px-14 py-5 text-center"
        style={{
          bottom: hasNgo ? '640px' : '340px',
          left: 0,
          right: 0,
          backgroundColor: BONE_WARM,
          borderTop: `2px solid ${HONEY}`,
          borderBottom: `2px solid ${HONEY}`,
        }}
        data-testid="handout-back-pricing"
      >
        <div style={{ fontSize: '24px', color: FOREST, fontFamily: FONT_HEAD }} className="italic font-semibold">
          {priceFloor}
        </div>
      </div>

      {/* Recap strip */}
      <div
        className="absolute px-14 text-center"
        style={{ bottom: hasNgo ? '520px' : '220px', left: 0, right: 0 }}
        data-testid="handout-back-recap"
      >
        <div
          style={{
            fontSize: '14px',
            color: TERRACOTTA,
            letterSpacing: '0.3em',
            marginBottom: '10px',
            fontFamily: FONT_MONO,
          }}
          className="uppercase font-semibold"
        >
          At a glance
        </div>
        <div
          className="flex items-center justify-center flex-wrap gap-x-6 gap-y-1"
          style={{ fontSize: '22px', color: FOREST, lineHeight: 1.3, fontFamily: FONT_BODY }}
        >
          {recapItems.map((item, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span style={{ color: HONEY }}>●</span>}
              <span className="font-semibold">{item}</span>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div
        className="absolute px-14 text-center"
        style={{ bottom: hasNgo ? '410px' : '110px', left: 0, right: 0 }}
      >
        {(xHandle || tiktokHandle) && (
          <div className="flex items-center justify-center gap-6 mb-2" style={{ color: FOREST, fontSize: '20px' }}>
            {xHandle && (
              <div className="flex items-center gap-2">
                <XGlyph size={22} />
                <span>@{xHandle.replace(/^@/, '')}</span>
              </div>
            )}
            {tiktokHandle && (
              <div className="flex items-center gap-2">
                <TikTokGlyph size={22} />
                <span>@{tiktokHandle.replace(/^@/, '')}</span>
              </div>
            )}
          </div>
        )}
        <div style={{ fontSize: '20px', color: TERRACOTTA, letterSpacing: '0.15em', fontFamily: FONT_MONO }}>
          {website}
        </div>
        {footerNote && (
          <div style={{ fontSize: '16px', color: MUTED_INK, marginTop: '10px', lineHeight: 1.4, fontFamily: FONT_BODY }} className="italic">
            {footerNote}
          </div>
        )}
      </div>

      {/* NGO co-brand footer */}
      {hasNgo && (
        <div
          className="absolute px-14 py-6"
          style={{
            bottom: 0,
            left: 0,
            right: 0,
            height: '390px',
            background: `linear-gradient(90deg, ${BONE_WARM} 0%, ${BONE} 100%)`,
            borderTop: `4px solid ${TERRACOTTA}`,
          }}
          data-testid="handout-back-ngo"
        >
          <div className="flex items-start gap-5">
            {ngoLogoSrc && (
              <div
                className="rounded-md overflow-hidden shrink-0"
                style={{
                  width: '100px',
                  height: '100px',
                  backgroundColor: BONE,
                  border: `2px solid ${BONE_BORDER}`,
                }}
              >
                <img
                  src={ngoLogoSrc}
                  alt={ngoName || 'NGO'}
                  crossOrigin="anonymous"
                  className="w-full h-full object-contain"
                />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div style={{ fontSize: '14px', color: TERRACOTTA, letterSpacing: '0.25em', fontFamily: FONT_MONO }} className="uppercase font-semibold">
                Gifted by
              </div>
              {ngoName && (
                <div style={{ fontSize: '28px', color: FOREST, lineHeight: 1.1, marginTop: '4px', fontFamily: FONT_HEAD }} className="italic">
                  {ngoName}
                </div>
              )}
              {ngoTagline && (
                <div style={{ fontSize: '16px', color: MUTED_INK, marginTop: '4px', fontFamily: FONT_BODY }} className="italic">
                  {ngoTagline}
                </div>
              )}
              <div
                className="mt-2 grid grid-cols-2 gap-x-6 gap-y-0.5"
                style={{ fontSize: '15px', color: FOREST_INK, lineHeight: 1.4, fontFamily: FONT_BODY }}
              >
                {ngoAddress && <div className="col-span-2">📍 {ngoAddress}</div>}
                {ngoPhone && <div>📞 {ngoPhone}</div>}
                {ngoEmail && <div className="truncate">✉ {ngoEmail}</div>}
                {ngoWebsite && <div className="col-span-2 truncate">🌐 {ngoWebsite}</div>}
              </div>
            </div>
          </div>

          <div className="mt-4 pt-3" style={{ borderTop: `1px dashed ${TERRACOTTA}` }}>
            <div
              style={{ fontSize: '13px', color: TERRACOTTA, letterSpacing: '0.2em', fontFamily: FONT_MONO }}
              className="uppercase font-semibold mb-2"
            >
              Notes / Local recommendations
            </div>
            <div className="space-y-4">
              <div style={{ borderBottom: `2px solid ${BONE_BORDER}`, height: '32px' }} />
              <div style={{ borderBottom: `2px solid ${BONE_BORDER}`, height: '32px' }} />
              <div style={{ borderBottom: `2px solid ${BONE_BORDER}`, height: '32px' }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
});
